import React, { useContext } from 'react';
import { faExclamationCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FincenContext } from './context';
import { Btn } from 'components/btn/Btn';
import { IFincen } from 'common/api/e-comm/cart';
import { formatDate } from 'common/utils/time';
import { Branding } from 'common/env';

const formatFlatPhone = (phone: string) => {
    if (phone && phone.length === 10) {
        const match = phone.match(/(\d{3})(\d{3})(\d{4})/);

        if (match) {
            const [, areaCode, firstPart, secondPart] = match;
            return `(${areaCode}) ${firstPart}-${secondPart}`;
        }
    }
    console.error('[formatFlatPhone]: Phone is not at least 10 characters');
    throw new Error('[formatFlatPhone]: Phone is not at least 10 characters');
};

export const RequiresInformationVerificationView = () => {
    return (
        <div className="border rounded-md p-5 shadow-md mb-5 border-brand">
            <Title />
            <FincenInfo />
            <Footer />
        </div>
    );
};

const FincenInfo = () => {
    const { moreInfoUrl } = useContext(FincenContext);
    return (
        <div className="font-semibold text-xs text-grey-2">
            <p>
                This order will result in you purchasing more than $10,000 worth of gift cards in
                the past 24 hours. We are required by the Financial Crimes Enforcement Network
                (FinCEN) to gather additional information before completing your purchase. We
                appreciate your patience as we work to comply with all federal regulations.
            </p>

            <p className="mt-5">
                For more information: <a href={moreInfoUrl}>{Branding} FinCEN Policy</a>
            </p>
        </div>
    );
};

const Title = () => (
    <div className="text-brand mb-8 font-semibold text-xl">
        <p>
            <FontAwesomeIcon icon={faExclamationCircle} size="lg" className="mr-2" />
            Please verify the previously submitted FinCEN information
        </p>
    </div>
);

const Header = ({ children }: React.PropsWithChildren) => (
    <p className="text-brand font-semibold text-xl mb-2">{children}</p>
);

// NOTE: Keep around in the case that we want this back once we're able to verify
// user's fincen info from react-web
export const UserInformation = () => {
    const { data } = useContext(FincenContext);

    return (
        <div className="flex flex-col sm:flex-row gap-10 text-sm text-grey-2">
            <Address {...(data.fincen as IFincen)} />
            <ContactInformation {...(data.fincen as IFincen)} />
        </div>
    );
};

const Address = ({
    firstName,
    lastName,
    address,
    address2,
    city,
    state,
    zip,
    suffix,
    streetName,
}: IFincen) => {
    return (
        <div>
            <Header>Address</Header>
            <p>{`${suffix ?? ''} ${firstName ?? ''} ${lastName ?? ''}`}</p>
            <p>{`${address ?? ''} ${streetName ?? ''} ${address2 ?? ''}`}</p>
            <p>{`${city ?? ''}, ${state ?? ''} ${zip ?? ''}`}</p>
        </div>
    );
};

const ContactInformation = ({
    phone,
    dateOfBirthDt,
    driversLicense,
    licenseIssueState,
}: IFincen) => {
    return (
        <div>
            <Header>Contact Information</Header>
            <div className="flex gap-4">
                <div>
                    <p>Phone</p>
                    <p>Date of Birth</p>
                    <p>Driver's License</p>
                    <p>State Issuing License</p>
                </div>
                <div>
                    <p>{phone ? formatFlatPhone(phone as string) : <>&nbsp;</>}</p>
                    <p>{dateOfBirthDt ? formatDate(dateOfBirthDt) : <>&nbsp;</>}</p>
                    <p>{driversLicense || <>&nbsp;</>}</p>
                    <p>{licenseIssueState || <>&nbsp;</>}</p>
                </div>
            </div>
        </div>
    );
};

const Footer = () => {
    const { editInfoUrl } = useContext(FincenContext);

    const editInformation = async () => {
        if (editInfoUrl) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (window as any).location.href = `${editInfoUrl}?returnUrl=${window.location.href}`;
        }
    };

    return (
        <div className="font-semibold text-sm text-grey-2 mt-8">
            <div className="mt-2 flex gap-5 flex-col-reverse sm:flex-row">
                <Btn onClick={editInformation}>Agree and Proceed</Btn>
            </div>
        </div>
    );
};
